import type { OrderDto, PlanFeature } from "@/shared/api/types";
import { useSession } from "@/shared/auth/session";
import type { TenantBannerState } from "@/shared/components/TenantStateBanner";
import { WORKSPACES } from "@/shared/constants/nav";
import { SUBSCRIPTION_ENDING_DAYS, TRIAL_ENDING_DAYS } from "@/shared/constants/limits";
import { useBillingSummaryQuery } from "@/shared/hooks/useBillingSummary";
import { useCatalogueQuery } from "@/shared/hooks/useCatalogue";
import { useEntitlementsQuery } from "@/shared/hooks/useEntitlements";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useWorkspaces } from "@/shared/hooks/useWorkspaces";
import { daysUntil } from "@/shared/utils/labels";
import { isSubscriptionOrder } from "@/shared/utils/orders";

/**
 * The add-on codes that own a workspace; the entitlements key items by the same names. A workspace that comes
 * with the base plan (`baseIncluded`, M24 Face Kiosk) is no add-on and is never counted as one.
 */
const MODULE_FEATURES: ReadonlySet<string> = new Set(
  WORKSPACES.filter((w) => !w.baseIncluded)
    .map((w) => w.planFeature)
    .filter((f): f is PlanFeature => f !== undefined),
);

export interface TenantBannerStates {
  /** Every notice this organisation is owed, in the order the shell stacks them. */
  banners: readonly TenantBannerState[];
  /**
   * Whether anything at all is up. **This is the whole reason the selector exists**: the billing
   * strip stands down on exactly this boolean, so "is a banner showing" has one answer on the page
   * rather than two components each deriving their own and disagreeing at the edges.
   */
  bannerUp: boolean;
  /** The issued invoice behind an `invoice_issued` / `overdue` banner, for the pay dialog. */
  openOrder: OrderDto | null;
}

/**
 * **The one reading of tenant state (redesign 2026-09, pass 03).**
 *
 * The escalation ladder is calm strip → warning fill → danger fill → `TenantStateBanner` takes over
 * and the strip is removed: *the strip owns the countdown, the banner owns the consequence, and they
 * are never both on screen.* The decision is explicit that this is "decided by one shared selector
 * rather than by two components agreeing" — so the shell and every billing strip read it from here.
 *
 * Before this, `ShellBanners` derived the states and `SubscriptionTile` carried a documented mirror
 * of the same conditions. The mirror had already drifted in two places, which is what a mirror does:
 * it stood down for an add-on trial on every workspace while the shell had been scoped to raise that
 * banner only on the add-on's own one (so the core Overview showed neither voice), and it treated a
 * missing `deleteScheduledAt` as a banner while the shell treated it as none.
 *
 * What is NOT decided here is the wording or the actions. The states are data; the shell attaches
 * its mutations and its dialog to them, because a selector that owned a `toast` would be a selector
 * nothing else could call.
 */
export function useTenantBannerStates(): TenantBannerStates {
  const { tenant } = useSession();
  const { can } = usePermissions();
  const usage = useEntitlementsQuery();
  /*
    The money half needs `GET /billing`, which is `billing:view` — Owner-only by preset (ADR-0006) —
    so an Admin sees the trial clocks (from the entitlements every member can read) and no invoice
    banner, and the query is never fired for them.
  */
  const billing = useBillingSummaryQuery(can("billing", "view"));
  const catalogue = useCatalogueQuery(can("billing", "view"));
  /** Which add-on's workspace the reader is in, if any — `undefined` in the core one. */
  const workspaceFeature = useWorkspaces().current.workspace.planFeature;

  const deleting = tenant.status === "deleting" || usage.data?.status === "deleting";
  const scheduledAt = usage.data?.deleteScheduledAt ?? null;
  const trial = usage.data?.trial ?? null;
  // An SMS recharge waiting for payment is not the subscription's invoice (M20): it is shown on Settings → SMS.
  const openOrder =
    (billing.data?.openOrders ?? []).find((o) => isSubscriptionOrder(o) && o.status === "issued") ?? null;
  const overdue = billing.data?.subscription?.status === "overdue";

  /**
   * A cancelled subscription in its last week (M11 §14). The whole point of `cancel_at_period_end` is
   * that nothing happens until the date, which also means nothing reminds anybody — so the shell counts
   * the last days down and offers the one-press way back, the same shape as the trial banner above it.
   */
  const endingAt =
    billing.data?.renewal?.cancelled === true || billing.data?.subscription?.cancelAtPeriodEnd === true
      ? (billing.data?.renewal?.at ?? billing.data?.subscription?.periodEnd ?? null)
      : null;

  /**
   * The first add-on whose own trial is nearly up; the base plan's clock is the banner above it.
   *
   * **On that add-on's OWN workspace only** (pass 03). The shell is one screen everybody watches, and
   * an organisation holding several add-ons would turn it into the place where all of them advertise
   * — a gym trial counting down over the access-control log is an interruption about a product the
   * reader is not in. The workspace the reader is standing in is keyed by the same `planFeature` the
   * entitlements use, so the two can never disagree about which clock belongs where.
   */
  const addonEnding = Object.entries(usage.data?.items ?? {})
    .filter(
      ([code, item]) =>
        item.kind !== "base" &&
        item.state === "trial" &&
        item.trialEndsAt !== null &&
        daysUntil(item.trialEndsAt) <= TRIAL_ENDING_DAYS &&
        code !== undefined &&
        // An add-on WITH a workspace escalates there and nowhere else. One without a workspace —
        // API access, webhooks — has no other home for a countdown, so the shell keeps it: the rule
        // exists to stop this screen advertising seven modules, not to silence the two that have
        // nowhere to go.
        (!MODULE_FEATURES.has(code) || code === workspaceFeature),
    )
    .map(([code, item]) => ({
      name: catalogue.data?.items.find((i) => i.code === code)?.name ?? code,
      endsAt: item.trialEndsAt as string,
    }))[0];

  const banners: TenantBannerState[] = [];
  if (deleting && scheduledAt) {
    // settings:manage: the cancel action is offered in the banner; everyone else reads where to find it.
    banners.push({ kind: "deleting", scheduledAt, canCancel: can("settings", "manage") });
  } else {
    if (trial?.expired) banners.push({ kind: "trial_expired", endedAt: trial.endsAt });
    else if (trial && trial.daysLeft <= TRIAL_ENDING_DAYS) {
      banners.push({ kind: "trial_ending", endsAt: trial.endsAt });
    }
    if (addonEnding) {
      banners.push({
        kind: "addon_trial_ending",
        itemName: addonEnding.name,
        endsAt: addonEnding.endsAt,
      });
    }
    if (endingAt && daysUntil(endingAt) <= SUBSCRIPTION_ENDING_DAYS) {
      banners.push({ kind: "subscription_ending", endsAt: endingAt });
    }
    if (openOrder) {
      banners.push(
        overdue
          ? {
              kind: "overdue",
              amountPaise: openOrder.totalPaise,
              graceUntil: billing.data?.subscription?.graceUntil ?? null,
            }
          : {
              kind: "invoice_issued",
              amountPaise: openOrder.totalPaise,
              dueAt: openOrder.dueAt,
            },
      );
    }
  }

  return { banners, bannerUp: banners.length > 0, openOrder };
}
